import React from 'react'
import gql from 'graphql-tag'
import PropTypes from 'prop-types'
import { getToken } from 'utils'
import LrsClient from 'utils/lrs_client'

import { fragments as TextParagraphFragments } from 'components/Paragraph/Text'
import { fragments as QuoteParagraphFragments } from 'components/Paragraph/Quote'
import { fragments as ConnectionParagraphFragments } from 'components/Paragraph/Connection'
import { fragments as GalleryParagraphFragments } from 'components/Paragraph/Gallery'
import { fragments as H5pParagraphFragments } from 'components/Paragraph/H5p'
import { fragments as ImageParagraphFragments } from 'components/Paragraph/Image'
import { fragments as LinkParagraphFragments } from 'components/Paragraph/Link'
import { fragments as VideoParagraphFragments } from 'components/Paragraph/Video'
import { fragments as HtmlParagraphFragments } from 'components/Paragraph/Html'
import { fragments as SectionParagraphFragments } from 'components/Paragraph/Section'
import { fragments as QuestionsBucketParagraphFragments } from 'components/Paragraph/QuestionsBucket'
import { fragments as DocumentParagraphFragments } from 'components/Paragraph/Document'

import {
  LayoutColumn,
  LayoutRow,
  fragments as LayoutColumnFragment,
} from 'components/Paragraph/LayoutGrid'

import { LayoutBox, fragments as BoxParagraphFragment } from 'components/Paragraph/LayoutBox'

import { fragments as ChartParagraphFragments } from 'components/Paragraph/Chart'
import { fragments as DataTableParagraphFragments } from 'components/Paragraph/DataTable'

import { Paragraph } from 'components'
import Credits from './Credits'

import styles from './Node.module.css'

const Node = ({ node }) => {
  const lrp = new LrsClient.Lrp({
    userAccessToken: getToken(),
    platform: 'Trilhas',
  })
  lrp.sendPageReadEvent(node.title)

  // Build credits array
  const credits = node.paragraphs
    .filter(item => item.entity && item.entity.creditsLabel && item.entity.credits)
    .map(item => ({
      label: item.entity.creditsLabel || null,
      content: item.entity.credits ? item.entity.credits.value : null,
    }))

  const paragraphsTree = paragraphs => {
    const elements = []
    const layoutsTypes = ['layout_column', 'layout_box']

    paragraphs.forEach((paragraph, index) => {
      const { entity } = paragraph
      if (entity !== undefined) {
        if (layoutsTypes.includes(entity.type)) {
          let children = []

          for (let i = index + 1; i <= index + parseInt(entity.affect); i++) {
            children.push({ ...paragraphs[i] })
            delete paragraphs[i]
          }

          if (children.length > 0) {
            children = paragraphsTree(children.map(p => p))
          }

          elements.push({
            ...entity,
            children,
          })
        } else {
          elements.push({
            ...entity,
          })
        }
      }
    })

    return elements
  }

  const renderParagraphs = paragraphs => {
    let row = []
    let container = []
    let teacherHeaderEnabled = false
    let teacherFooterEnabled = false
    let statusHeaderFooter = false

    const html = paragraphs.map((paragraph, index) => {
      container = []

      if (paragraph.type !== 'layout_column' && row.length > 0) {
        container.push(<LayoutRow>{row}</LayoutRow>)
        row = []
      }

      if (paragraph.type === 'layout_box') {
        const boxType = paragraph.typeOf?.entity
        container.push(
          <LayoutBox
            key={index}
            alternateTitle={boxType?.fieldAlternateTitle?.value}
            presentationMode={boxType?.fieldBoxPresentation}
            borderEnabled={boxType?.fieldBorderBoolean}
            backgroundColorEnabled={boxType?.fieldBackgroundBoolean}
            fontColorEnabled={boxType?.fieldFontBoolean}
            headerImageEnabled={boxType?.fieldHeaderImageBoolean}
            footerImageEnabled={boxType?.fieldFooterImageBoolean}
            borderColor={boxType?.fieldBorderColor.color}
            backgroundColor={boxType?.fieldBackgroundColor.color}
            fontColor={boxType?.fieldFontColor.color}
            headerImageUrl={boxType?.fieldHeaderImage?.entity.image.url}
            footerImageUrl={boxType?.fieldFooterImage?.entity.image.url}
            iconImageUrl={boxType?.fieldTeaserMedia?.entity.image.url}
          >
            {' '}
            {renderParagraphs(paragraph.children)}
          </LayoutBox>,
        )
      }

      if (paragraph.type === 'layout_column') {
        row.push(
          <LayoutColumn size={paragraph.size} key={index}>
            {renderParagraphs(paragraph.children)}
          </LayoutColumn>,
        )
        return ''
      }

      if (paragraph.avaliableTeacherOnly) {
        if (index !== 0 && paragraphs[index - 1] && paragraphs[index - 1].avaliableTeacherOnly) {
          teacherHeaderEnabled = false
          statusHeaderFooter = true
        } else {
          teacherHeaderEnabled = true
          statusHeaderFooter = false
        }

        if (
          !paragraphs[index + 1] ||
          (paragraphs[index + 1] && !paragraphs[index + 1].avaliableTeacherOnly)
        ) {
          teacherFooterEnabled = true
          statusHeaderFooter = false
        }
      }

      container.push(
        <Paragraph
          key={paragraph.entityId}
          headerTeacher={teacherHeaderEnabled}
          footerTeacher={teacherFooterEnabled}
          noHeaderNoFooter={statusHeaderFooter}
          entity={paragraph}
        />,
      )
      return container
    })

    if (row.length > 0) {
      html.push(<LayoutRow key='99'>{row}</LayoutRow>)
    }
    return html
  }

  const paragraphs = paragraphsTree([...node.paragraphs])
  const title = node?.alternateTitle?.value || node.title

  return (
    <section className={!node.status ? styles.unpublished : ''}>
      <h1 className={styles.title} dangerouslySetInnerHTML={{ __html: title }} />
      {renderParagraphs(paragraphs)}

      {credits.length > 0 && <Credits items={credits} />}
    </section>
  )
}

Node.propTypes = {
  node: PropTypes.shape({
    nid: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    paragraphs: PropTypes.array,
  }),
}

const fragments = {
  article: gql`
    fragment NodeArticleFragment on NodeArticle {
      nid
      title
      alternateTitle: fieldAlternateTitle {
        value
      }
      weight: draggableViewsWeight
      status

      channel: fieldChannel {
        ... on FieldNodeFieldChannel {
          entity {
            ...TaxonomyTermChannelFragment
          }
        }
      }

      tags: fieldTags {
        targetId
        ... on FieldNodeFieldTags {
          entity {
            ... on TaxonomyTerm {
              name
            }
          }
        }
      }

      paragraphs: fieldParagraphs {
        entity {
          entityId
          type: entityBundle

          ...TextParagraphFragment

          ...QuoteParagraphFragment

          ...ConnectionParagraphFragment

          ...GalleryParagraphFragment

          ...H5pParagraphFragment

          ...ImageParagraphFragment

          ...LinkParagraphFragment

          ...VideoParagraphFragment

          ...HtmlParagraphFragment

          ...LayoutColumnFragment

          ...ChartParagraphFragment

          ...DataTableParagraphFragment

          ...SectionParagraphFragment

          ...QuestionsBucketParagraphFragment

          ...BoxParagraphFragment
          ...DocumentParagraphFragment
        }
      }
      avaliableTeacherOnly: fieldAvailableTeacherOnly
    }

    ${TextParagraphFragments.paragraph}
    ${QuoteParagraphFragments.paragraph}
    ${ConnectionParagraphFragments.paragraph}
    ${GalleryParagraphFragments.paragraph}
    ${H5pParagraphFragments.paragraph}
    ${ImageParagraphFragments.paragraph}
    ${LinkParagraphFragments.paragraph}
    ${VideoParagraphFragments.paragraph}
    ${HtmlParagraphFragments.paragraph}
    ${LayoutColumnFragment.paragraph}
    ${ChartParagraphFragments.paragraph}
    ${DataTableParagraphFragments.paragraph}
    ${SectionParagraphFragments.paragraph}
    ${QuestionsBucketParagraphFragments.paragraph}
    ${BoxParagraphFragment.paragraph}
    ${DocumentParagraphFragments.paragraph}

    fragment TaxonomyTermChannelFragment on TaxonomyTermChannel {
      tid
      title: entityLabel
      url: entityUrl {
        path
      }
      media: fieldTeaserMedia {
        entity {
          thumbnail {
            alt
            title
            derivative(style: CARD_360X290) {
              url
            }
          }
        }
      }
    }
  `,
}

export { Node, fragments }
